<template>
  <div class="trbo-a-question-renderer">
    <component
      :is="pageTypeComponent"
      :key="question.id"
      :question="question"
      :question-data="questionData"
      :selected="selected"
      :option-search-query="optionSearchQuery"
      @select="onSelect"
      @submit="onSubmit"
    />
  </div>
</template>

<script>
// @flow

import { type QuestionSelection, isMultipleChoice, isInput, isRange, isFileUpload, isMultipleInput } from '../../../../common/wizard'
import SingleChoice from './SingleChoice/SingleChoice.vue'
import MultipleChoice from './MultipleChoice/MultipleChoice.vue'
import Gallery from './Gallery/Gallery.vue'
import QuestionInput from './QuestionInput/QuestionInput.vue'
import Range from './Range/Range.vue'
import Video from './Video/Video.vue'
import FileUpload from './FileUpload/FileUpload.vue'
import MultipleInput from './MultipleInput/MultipleInput.vue'
import Image from './Image/Image.vue'

export default {
  components: {
    SingleChoice,
    MultipleChoice,
    Gallery,
    QuestionInput,
    Range,
    Video,
    FileUpload,
    MultipleInput,
    // Using alias because Image is built-in or reserved HTML element
    ImagePageType: Image
  },
  props: {
    question: {
      type: Object, // WizardPage
      required: true
    },
    questionData: {
      type: Object, // WizardPageData<SingleChoiceData | MultipleChoiceData | InputData | RangeData | ProductData | MultipleInputData | VideoData | ImageData>
      required: true
    },
    selections: {
      type: Object, // QuestionSelection
      required: true
    },
    optionSearchQuery: {
      type: String,
      default: null
    }
  },
  computed: {
    pageTypeComponent (): string {
      const components = {
        single_choice: 'SingleChoice',
        multiple_choice: 'MultipleChoice',
        gallery: 'Gallery',
        image: 'ImagePageType',
        input: 'QuestionInput',
        range: 'Range',
        video: 'Video',
        file_upload: 'FileUpload',
        multiple_input: 'MultipleInput'
      }
      const pageType = components[this.question.pageType]
      return pageType ?? null
    },
    selected () {
      if (isMultipleChoice(this.question)) {
        if (this.selections[this.question.id]) {
          return this.selections[this.question.id].map(({ uuid }) => uuid)
        }
        return []
      } else if (isInput(this.question)) {
        if (this.selections[this.question.id]) {
          return this.selections[this.question.id][0].value
        }
      } else if (isRange(this.question) || isFileUpload(this.question)) {
        if (this.selections[this.question.id]) {
          return this.selections[this.question.id][0].value
        }
        return []
      } else if (isMultipleInput(this.question)) {
        if (this.selections[this.question.id]) {
          return this.selections[this.question.id].map(option => option.value)
        }
        return []
      } else {
        if (this.selections[this.question.id]) {
          return this.selections[this.question.id][0].uuid
        }
      }
      return null
    }
  },
  methods: {
    onSelect ({ question, option }) {
      const payload: QuestionSelection = {
        options: this.getOptions({ question, option })
      }
      this.$emit('select', payload)
    },
    getOptions ({ question, option }) {
      const { uuid } = option
      let output = [uuid]
      if (isMultipleChoice(question)) {
        if (Array.isArray(this.selected) && this.selected.includes(uuid)) {
          output = this.selected.filter((_uuid) => _uuid !== uuid)
        } else {
          output = output.concat(this.selected)
        }
      }
      output = output.map(uuid => ({ uuid }))
      return output
    },
    onSubmit ({ value, options, forceNextPage }) {
      const payload: QuestionSelection = {
        options: options ?? [{ value }],
        forceNextPage
      }
      this.$emit('select', payload)
    }
  }
}
</script>

<style scoped src="./wizard-page.scss" />
